/**
 * 전역 필터 상태 (DESIGN §8.7).
 *
 * **URL 쿼리스트링 양방향 동기화** — 필터 상태가 곧 공유 가능한 링크가 된다.
 * 연구 도구에서 "내가 본 그 화면"을 그대로 전달할 수 있어야 한다.
 */

import { create } from 'zustand'

import type { QueryFilters } from '@/lib/api'
import { MODES, type Mode } from '@/lib/types'

export interface FilterState {
  from: string | null
  to: string | null
  mode: Mode
  busi: string[]
  zcode: string[]
  zscode: string[]
  kind: string[]
  chgerType: string[]
  maker: string[]
  /** **특정 연식** (복수 선택). 기간과 AND 로 걸린다 — 붙어 있지 않은 연식을
   *  견주려면 범위로는 표현되지 않는다 (§8.10.2) */
  year: number[]
  yearFrom: number | null
  yearTo: number | null
  floor: 'F' | 'B' | null
  minCoverage: number
}

export const DEFAULT_FILTERS: FilterState = {
  from: null,
  to: null,
  mode: 'standard',
  busi: [],
  zcode: [],
  zscode: [],
  kind: [],
  chgerType: [],
  maker: [],
  year: [],
  yearFrom: null,
  yearTo: null,
  floor: null,
  minCoverage: 0.5,
}

const LIST_KEYS = ['busi', 'zcode', 'zscode', 'kind', 'chgerType', 'maker'] as const
const URL_KEY: Record<(typeof LIST_KEYS)[number], string> = {
  busi: 'busi',
  zcode: 'zcode',
  zscode: 'zscode',
  kind: 'kind',
  chgerType: 'chger_type',
  maker: 'maker',
}

function isMode(value: string | null): value is Mode {
  return value != null && (MODES as readonly string[]).includes(value)
}

export function filtersFromUrl(search: string): FilterState {
  const p = new URLSearchParams(search)
  const next: FilterState = { ...DEFAULT_FILTERS }

  const mode = p.get('mode')
  if (isMode(mode)) next.mode = mode
  next.from = p.get('from')
  next.to = p.get('to')
  for (const key of LIST_KEYS) next[key] = p.getAll(URL_KEY[key])
  // 연식은 숫자 목록이라 LIST_KEYS(문자열) 와 따로 다룬다
  next.year = p.getAll('year').map(Number).filter((n) => Number.isFinite(n))

  const yearFrom = p.get('year_from')
  const yearTo = p.get('year_to')
  if (yearFrom) next.yearFrom = Number(yearFrom)
  if (yearTo) next.yearTo = Number(yearTo)

  const floor = p.get('floor')
  if (floor === 'F' || floor === 'B') next.floor = floor

  const minCoverage = p.get('min_coverage')
  if (minCoverage != null && !Number.isNaN(Number(minCoverage))) {
    next.minCoverage = Number(minCoverage)
  }
  return next
}

export function filtersToUrl(f: FilterState): string {
  const p = new URLSearchParams()
  // 기본값은 URL 에 넣지 않는다 — 링크가 짧을수록 공유하기 좋다
  if (f.mode !== DEFAULT_FILTERS.mode) p.set('mode', f.mode)
  if (f.from) p.set('from', f.from)
  if (f.to) p.set('to', f.to)
  for (const key of LIST_KEYS) for (const v of f[key]) p.append(URL_KEY[key], v)
  for (const y of f.year) p.append('year', String(y))
  if (f.yearFrom != null) p.set('year_from', String(f.yearFrom))
  if (f.yearTo != null) p.set('year_to', String(f.yearTo))
  if (f.floor) p.set('floor', f.floor)
  if (f.minCoverage !== DEFAULT_FILTERS.minCoverage) {
    p.set('min_coverage', String(f.minCoverage))
  }
  const qs = p.toString()
  return qs ? `?${qs}` : ''
}

export function toQueryFilters(f: FilterState): QueryFilters {
  return {
    mode: f.mode,
    from: f.from,
    to: f.to,
    busi: f.busi,
    zcode: f.zcode,
    zscode: f.zscode,
    kind: f.kind,
    chgerType: f.chgerType,
    maker: f.maker,
    year: f.year,
    yearFrom: f.yearFrom,
    yearTo: f.yearTo,
    floor: f.floor,
    minCoverage: f.minCoverage,
  }
}

/** TanStack Query 키용 — 순서가 흔들리지 않는 직렬화 (§8.7). */
export function filterKey(f: FilterState): string {
  return JSON.stringify({
    mode: f.mode,
    from: f.from,
    to: f.to,
    busi: [...f.busi].sort(),
    zcode: [...f.zcode].sort(),
    zscode: [...f.zscode].sort(),
    kind: [...f.kind].sort(),
    chgerType: [...f.chgerType].sort(),
    maker: [...f.maker].sort(),
    year: [...f.year].sort((a, b) => a - b),
    yearFrom: f.yearFrom,
    yearTo: f.yearTo,
    floor: f.floor,
    minCoverage: f.minCoverage,
  })
}

interface FilterStore extends FilterState {
  set: (patch: Partial<FilterState>) => void
  setMode: (mode: Mode) => void
  toggleList: (key: (typeof LIST_KEYS)[number], value: string) => void
  reset: () => void
}

function syncUrl(state: FilterState) {
  if (typeof window === 'undefined') return
  // 해시(현재 페이지)는 필터 소관이 아니다 — 지우면 필터를 만질 때마다 개요로 튄다
  const next = `${window.location.pathname}${filtersToUrl(state)}${window.location.hash}`
  window.history.replaceState(null, '', next)
}

export const useFilters = create<FilterStore>((set, get) => ({
  ...(typeof window === 'undefined'
    ? DEFAULT_FILTERS
    : filtersFromUrl(window.location.search)),

  set: (patch) => {
    set(patch)
    syncUrl({ ...get(), ...patch })
  },
  setMode: (mode) => {
    set({ mode })
    syncUrl({ ...get(), mode })
  },
  toggleList: (key, value) => {
    const current = get()[key]
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    set({ [key]: next } as Partial<FilterState>)
    syncUrl({ ...get(), [key]: next })
  },
  reset: () => {
    set(DEFAULT_FILTERS)
    syncUrl(DEFAULT_FILTERS)
  },
}))
